// src/stage2/FirstPersonControls.jsx
import { useEffect, useRef, useState } from "react";
import { useThree, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { playgroundColliders, groundMeshes } from "./collision/CollisionStore";

export default function FirstPersonControls({
  envVariant,
  setHasBike,
  playerPosRef,
  onWalkStateChange,
  nearBikeId,
  activeBikeId,
  onRideStart,
  onRideEnd,
  bikePosById,
  onRideUpdate,
}) {
  const { camera, gl } = useThree();

  // --- config camera ---
  const EYE_OFFSET = 0.95;
  const WALK_SPEED = 2.0;
  const STRAFE_SPEED = 3.0;
  const SENSITIVITY = 0.0022;
  const PITCH_LIMIT = Math.PI / 2 - 0.05;

  // separate pitch limits while riding
  const PITCH_LIMIT_RIDE_UP = 0.4;
  const PITCH_LIMIT_RIDE_DOWN = 0.8;

  const bounds = { minX: -34, maxX: 35.5, minZ: -13, maxZ: 22.75 };
  //TO-DO: Change bounds for "Flying bike" mode 
  const bikeBounds = { minX: -34, maxX: 35.5, minZ: -13, maxZ: 22.75 };

  const PLAYER_RADIUS = 0.5;
  const TAP_HOLD_MS = 180;

  // --- state/refs ---
  const [isLocked, setIsLocked] = useState(false);
  const [walkHeld, setWalkHeld] = useState(false);

  const yaw = useRef(0);
  const pitch = useRef(0);

  const keys = useRef({
    w: false,
    a: false,
    s: false,
    d: false,
    ArrowUp: false,
    ArrowDown: false,
    ArrowLeft: false,
    ArrowRight: false,
  });

  const didInitRef = useRef(false);
  const raycasterRef = useRef(new THREE.Raycaster());
  const wasMovingRef = useRef(false);

  //////////////////////////
  // SPAWN CONTROLLER FOR WHERE CAMERA (PLAYER) SPAWNS WHEN CLASSROOM/PLAYGROUND SCENE SWITCHES
  // DoorTrigger.jsx decides "when" env changes and makes request
  // Stage2Enviroment desicdes "What' is mounted (playground or classroom"
  // This component decides "where the player is"
  const SPAWNS = {
  playground: {
    pos: new THREE.Vector3(0, EYE_OFFSET, 22.5),
    yaw: Math.PI + 3,
    pitch: 0,
  },
  classroom: {
    pos: new THREE.Vector3(11, EYE_OFFSET, 0.2),
    yaw: Math.PI + 4.75,       // face into room
    pitch: 0,
  },
};

useEffect(() => {
  const spawn = SPAWNS[envVariant];
  if (!spawn) return;

  // 🚫 never teleport while riding
  if (ridingRef.current) return;

  camera.position.copy(spawn.pos);
  yaw.current = spawn.yaw;
  pitch.current = spawn.pitch;

  camera.rotation.set(
    pitch.current,
    yaw.current,
    0,
    "YXZ"
  );

  // reset transient motion
  setWalkHeld(false);
  wasMovingRef.current = false;

  if (playerPosRef?.current) {
    playerPosRef.current.copy(camera.position);
  }

  console.log("[FPC] Spawned in", envVariant);
}, [envVariant]);

  ///////////////////////////
  // BIKE RIDE & BIKE FLY
  // latest values inside event listeners
  const nearBikeIdRef = useRef(nearBikeId);
  const onRideStartRef = useRef(onRideStart);
  const onRideEndRef = useRef(onRideEnd);

  useEffect(() => {
    nearBikeIdRef.current = nearBikeId;
  }, [nearBikeId]);

  useEffect(() => {
    onRideStartRef.current = onRideStart;
  }, [onRideStart]);

  useEffect(() => {
    onRideEndRef.current = onRideEnd;
  }, [onRideEnd]);

  // Riding state
  const ridingRef = useRef(false);

  // Seat offset relative to bike position
  const SEAT_OFFSET = new THREE.Vector3(0, 1.95, 0.35);

  // Lerp strength per second
  const MOUNT_LERP = 20;

  // helpers (no per-frame allocations)
  const seatOffsetWorldRef = useRef(new THREE.Vector3());
  const seatTargetRef = useRef(new THREE.Vector3());
  const yawEulerRef = useRef(new THREE.Euler(0, 0, 0, "YXZ"));
  const forwardRef = useRef(new THREE.Vector3());
  const originRef = useRef(new THREE.Vector3());

  // terrain hug
  const BIKE_GROUND_OFFSET = 0.0;
  const BIKE_GROUND_LERP = 18;

  // flying
  const isFlyingRef = useRef(false);
  const flyVelYRef = useRef(0);
  const flyEnergyRef = useRef(0);
  const flapQueuedRef = useRef(false);

  const TAKEOFF_SPEED = 5.2;
  const MAX_FLIGHT_HEIGHT = 4.0;
  const GRAVITY = 7.5;
  const FLAP_IMPULSE = 4.2;
  const FLAP_COST = 0.18;
  const ENERGY_RECHARGE_RATE = 0.65;
  const ENERGY_MIN_TO_FLAP = 0.06;
  const LANDING_SNAP = 0.06;
  const FLY_DAMP = 1.6;

  // momentum
  const bikeSpeedRef = useRef(0);

  const BIKE_MAX_SPEED = 8.0;
  const BIKE_ACCEL = 9.0;
  const BIKE_BRAKE = 12.0;
  const BIKE_FRICTION = 7.0;

  const coerceVec3 = (p) => {
    if (!p) return null;
    if (p.isVector3) return p;
    if (typeof p.x === "number" && typeof p.y === "number" && typeof p.z === "number") {
      return new THREE.Vector3(p.x, p.y, p.z);
    }
    return null;
  };

  const bikeWorldPosRef = useRef(new THREE.Vector3());
  const bikeYawRef = useRef(0);

  useEffect(() => {
    const ridingNow = !!activeBikeId;
    ridingRef.current = ridingNow;

    if (typeof setHasBike === "function") setHasBike(ridingNow);

    if (!ridingNow) {
      isFlyingRef.current = false;
      flyVelYRef.current = 0;
      flapQueuedRef.current = false;
    }
  }, [activeBikeId, setHasBike]);

  useEffect(() => {
    if (!activeBikeId) return;
    const raw = bikePosById?.[activeBikeId];
    const vec = coerceVec3(raw);
    if (vec) bikeWorldPosRef.current.copy(vec);
    bikeYawRef.current = yaw.current;

    flyEnergyRef.current = Math.max(flyEnergyRef.current, 0.25);
  }, [activeBikeId, bikePosById]);

  const mountBike = () => {
    const id = nearBikeIdRef.current;
    if (!id) return;
    if (activeBikeId) return;
    onRideStartRef.current?.(id);
  };

  const dismountBike = () => {
    if (!activeBikeId) return;

    // ✅ Force landing if flying, then dismount
    if (isFlyingRef.current) {
      isFlyingRef.current = false;
      flyVelYRef.current = 0;
    }

    onRideEndRef.current?.();
  };

  const toggleMount = () => {
    if (ridingRef.current) dismountBike();
    else mountBike();
  };

  /////////////////////////////
  // MOUSE MOVEMENT CONTROLS
  useEffect(() => {
    const el = gl.domElement;

    if (!didInitRef.current) {
      camera.position.set(-11, EYE_OFFSET, -11);
      yaw.current = Math.PI + 0.72;
      pitch.current = -0.02;
      camera.rotation.set(pitch.current, yaw.current, 0, "YXZ");
      didInitRef.current = true;
    }

    const requestLock = () => {
      if (document.pointerLockElement !== el) el.requestPointerLock();
    };

    const exitLock = () => {
      if (document.pointerLockElement === el) document.exitPointerLock();
    };

    const onLockChange = () => {
      const locked = document.pointerLockElement === el;
      setIsLocked(locked);
      el.style.cursor = locked ? "none" : "auto";
      if (!locked) setWalkHeld(false);
    };

    const onLockError = () => {
      console.warn("Pointer lock error");
      setIsLocked(false);
      el.style.cursor = "auto";
    };

    const clampPitch = () => {
      if (ridingRef.current) {
        pitch.current = THREE.MathUtils.clamp(
          pitch.current,
          -PITCH_LIMIT_RIDE_UP,
          PITCH_LIMIT_RIDE_DOWN
        );
      } else {
        pitch.current = THREE.MathUtils.clamp(pitch.current, -PITCH_LIMIT, PITCH_LIMIT);
      }
    };

    const onMouseMove = (e) => {
      if (document.pointerLockElement !== el) return;
      const dx = e.movementX || 0;
      const dy = e.movementY || 0;

      yaw.current -= dx * SENSITIVITY;
      pitch.current -= dy * SENSITIVITY;

      clampPitch();
      camera.rotation.set(pitch.current, yaw.current, 0, "YXZ");
    };

    let holdTimer = null;
    let heldAsWalk = false;

    const onMouseDown = (e) => {
      if (e.button !== 0) return;

      if (document.pointerLockElement !== el) {
        requestLock();
        return;
      }

      heldAsWalk = false;
      if (holdTimer) window.clearTimeout(holdTimer);

      holdTimer = window.setTimeout(() => {
        heldAsWalk = true;
        setWalkHeld(true);
      }, TAP_HOLD_MS);
    };

    const onMouseUp = () => {
      if (holdTimer) window.clearTimeout(holdTimer);
      holdTimer = null;

      if (heldAsWalk) {
        setWalkHeld(false);
        heldAsWalk = false;
        return;
      }

      setWalkHeld(false);

      if (nearBikeIdRef.current) toggleMount();
    };

    const isIgnorableKey = (k) =>
      k === "Shift" || k === "Control" || k === "Alt" || k === "Meta" || k === "CapsLock" || k === "Tab";

    const onKeyDown = (e) => {
      const k = e.key;

      if (k in keys.current) keys.current[k] = true;

      if (k === "l" || k === "L") {
        if (document.pointerLockElement === el) exitLock();
        else requestLock();
        return;
      }

      if (k === " " || k === "Spacebar") {
        e.preventDefault();
        if (ridingRef.current) flapQueuedRef.current = true;
        return;
      }

      if (k === "e" || k === "E") {
        dismountBike();
        return;
      }

      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", " "].includes(k)) {
        e.preventDefault();
      }

      if (nearBikeIdRef.current && !ridingRef.current && k !== "Escape" && !isIgnorableKey(k)) {
        mountBike();
      }
    };

    const onKeyUp = (e) => {
      const k = e.key;
      if (k in keys.current) keys.current[k] = false;

      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", " "].includes(k)) {
        e.preventDefault();
      }
    };

    const touch = { x: 0, y: 0, has: false };
    let touchHoldTimer = null;
    let touchHeldAsWalk = false;

    const onTouchStart = (e) => {
      const t = e.touches[0];
      touch.x = t.clientX;
      touch.y = t.clientY;
      touch.has = true;

      touchHeldAsWalk = false;
      if (touchHoldTimer) window.clearTimeout(touchHoldTimer);

      touchHoldTimer = window.setTimeout(() => {
        touchHeldAsWalk = true;
        setWalkHeld(true);
      }, TAP_HOLD_MS);
    };

    const onTouchMove = (e) => {
      const t = e.touches[0];
      if (!touch.has) {
        touch.x = t.clientX;
        touch.y = t.clientY;
        touch.has = true;
        return;
      }
      const dx = t.clientX - touch.x;
      const dy = t.clientY - touch.y;
      touch.x = t.clientX;
      touch.y = t.clientY;

      yaw.current -= dx * SENSITIVITY;
      pitch.current -= dy * SENSITIVITY;

      clampPitch();
      camera.rotation.set(pitch.current, yaw.current, 0, "YXZ");
    };

    const onTouchEnd = () => {
      if (touchHoldTimer) window.clearTimeout(touchHoldTimer);
      touchHoldTimer = null;

      if (touchHeldAsWalk) {
        setWalkHeld(false);
        touchHeldAsWalk = false;
        touch.has = false;
        return;
      }

      setWalkHeld(false);
      touch.has = false;

      if (nearBikeIdRef.current) toggleMount();
    };

    el.addEventListener("click", requestLock);
    document.addEventListener("pointerlockchange", onLockChange);
    document.addEventListener("pointerlockerror", onLockError);

    el.addEventListener("mousemove", onMouseMove);
    el.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mouseup", onMouseUp);
    el.addEventListener("contextmenu", (e) => e.preventDefault());

    window.addEventListener("keydown", onKeyDown, { passive: false });
    window.addEventListener("keyup", onKeyUp, { passive: false });

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("click", requestLock);
      document.removeEventListener("pointerlockchange", onLockChange);
      document.removeEventListener("pointerlockerror", onLockError);

      el.removeEventListener("mousemove", onMouseMove);
      el.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mouseup", onMouseUp);

      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);

      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);

      if (holdTimer) window.clearTimeout(holdTimer);
      if (touchHoldTimer) window.clearTimeout(touchHoldTimer);
    };
  }, [camera, gl]);

  const getGroundY = (x, z) => {
    if (groundMeshes.length === 0) return 0;
    const raycaster = raycasterRef.current;
    originRef.current.set(x, 100, z);
    raycaster.set(originRef.current, new THREE.Vector3(0, -1, 0));
    const hits = raycaster.intersectObjects(groundMeshes, true);
    return hits.length > 0 ? hits[0].point.y : 0;
  };

  useFrame((_, dt) => {
    // ✅ ALWAYS keep playerPosRef synced for NPC proximity
    if (playerPosRef?.current) {
      playerPosRef.current.copy(camera.position);
    }

    const rawBikePos = bikePosById?.[activeBikeId];
    const bikePos = coerceVec3(rawBikePos);

    const BIKE_MODEL_YAW_OFFSET = Math.PI;

    if (ridingRef.current && activeBikeId && bikePos) {
      if (bikeWorldPosRef.current.lengthSq() === 0) {
        bikeWorldPosRef.current.copy(bikePos);
        const gy = getGroundY(bikeWorldPosRef.current.x, bikeWorldPosRef.current.z);
        bikeWorldPosRef.current.y = gy + BIKE_GROUND_OFFSET;
      }

      bikeYawRef.current = yaw.current;

      const fwd = walkHeld || keys.current.w || keys.current.ArrowUp;
      const back = keys.current.s || keys.current.ArrowDown;

      const targetSpeed = fwd ? BIKE_MAX_SPEED : back ? -BIKE_MAX_SPEED * 0.55 : 0;
      const currentSpeed = bikeSpeedRef.current;

      let newSpeed = currentSpeed;
      if (targetSpeed > currentSpeed) {
        newSpeed = Math.min(targetSpeed, currentSpeed + BIKE_ACCEL * dt);
      } else if (targetSpeed < currentSpeed) {
        const decel = back ? BIKE_BRAKE : BIKE_FRICTION;
        newSpeed = Math.max(targetSpeed, currentSpeed - decel * dt);
      } else if (targetSpeed === 0) {
        const sign = Math.sign(currentSpeed);
        const mag = Math.max(0, Math.abs(currentSpeed) - BIKE_FRICTION * dt);
        newSpeed = mag * sign;
      }
      bikeSpeedRef.current = newSpeed;

      if (Math.abs(newSpeed) > 0.0001) {
        yawEulerRef.current.set(0, bikeYawRef.current, 0);
        forwardRef.current.set(0, 0, -1).applyEuler(yawEulerRef.current);

        bikeWorldPosRef.current.addScaledVector(forwardRef.current, newSpeed * dt);

        bikeWorldPosRef.current.x = THREE.MathUtils.clamp(
          bikeWorldPosRef.current.x, bikeBounds.minX, bikeBounds.maxX
        );
        bikeWorldPosRef.current.z = THREE.MathUtils.clamp(
          bikeWorldPosRef.current.z, bikeBounds.minZ, bikeBounds.maxZ
        );
      }

      const groundY = getGroundY(bikeWorldPosRef.current.x, bikeWorldPosRef.current.z) + BIKE_GROUND_OFFSET;
      const heightAboveGround = bikeWorldPosRef.current.y - groundY;

      const grounded = !isFlyingRef.current && Math.abs(heightAboveGround) < 0.12;
      if (grounded && Math.abs(bikeSpeedRef.current) > 1.2) {
        flyEnergyRef.current = THREE.MathUtils.clamp(
          flyEnergyRef.current + ENERGY_RECHARGE_RATE * dt, 0, 1
        );
      }

      if (flapQueuedRef.current) {
        flapQueuedRef.current = false;

        const speedOk = Math.abs(bikeSpeedRef.current) >= TAKEOFF_SPEED;
        const energyOk = flyEnergyRef.current >= ENERGY_MIN_TO_FLAP;

        if (!isFlyingRef.current) {
          if (speedOk && energyOk) {
            isFlyingRef.current = true;
            flyVelYRef.current = Math.max(flyVelYRef.current, FLAP_IMPULSE * 0.9);
            flyEnergyRef.current = Math.max(0, flyEnergyRef.current - FLAP_COST);
          }
        } else {
          if (energyOk) {
            flyVelYRef.current += FLAP_IMPULSE;
            flyEnergyRef.current = Math.max(0, flyEnergyRef.current - FLAP_COST);
          }
        }
      }

      if (isFlyingRef.current) {
        flyVelYRef.current -= GRAVITY * dt;
        flyVelYRef.current -= flyVelYRef.current * FLY_DAMP * dt;

        bikeWorldPosRef.current.y += flyVelYRef.current * dt;

        const maxY = groundY + MAX_FLIGHT_HEIGHT;
        if (bikeWorldPosRef.current.y > maxY) {
          bikeWorldPosRef.current.y = maxY;
          flyVelYRef.current = Math.min(0, flyVelYRef.current);
        }

        if (bikeWorldPosRef.current.y <= groundY + LANDING_SNAP) {
          bikeWorldPosRef.current.y = groundY;
          flyVelYRef.current = 0;
          isFlyingRef.current = false;
        }
      } else {
        const aY = 1 - Math.exp(-BIKE_GROUND_LERP * dt);
        bikeWorldPosRef.current.y = THREE.MathUtils.lerp(bikeWorldPosRef.current.y, groundY, aY);
      }

      onRideUpdate?.(activeBikeId, {
        position: bikeWorldPosRef.current.clone(),
        yaw: bikeYawRef.current + BIKE_MODEL_YAW_OFFSET,
        flying: isFlyingRef.current,
        flyEnergy: flyEnergyRef.current,
      });

      yawEulerRef.current.set(0, bikeYawRef.current, 0);
      seatOffsetWorldRef.current.copy(SEAT_OFFSET).applyEuler(yawEulerRef.current);
      seatTargetRef.current.copy(bikeWorldPosRef.current).add(seatOffsetWorldRef.current);

      const alpha = 1 - Math.exp(-MOUNT_LERP * dt);
      camera.position.lerp(seatTargetRef.current, alpha);

      const isMovingRide = Math.abs(bikeSpeedRef.current) > 0.15;
      if (onWalkStateChange && isMovingRide !== wasMovingRef.current) {
        wasMovingRef.current = isMovingRide;
        onWalkStateChange(isMovingRide);
      }

      return;
    }

    // WALKING MODE
    const f = walkHeld || keys.current.w || keys.current.ArrowUp;
    const b = keys.current.s || keys.current.ArrowDown;
    const l = keys.current.a || keys.current.ArrowLeft;
    const r = keys.current.d || keys.current.ArrowRight;

    const move = new THREE.Vector3();
    if (f) move.z -= 1;
    if (b) move.z += 1;
    if (l) move.x -= 1;
    if (r) move.x += 1;

    let isMoving = move.lengthSq() > 0;

    if (isMoving) {
      move.normalize();

      const yawOnly = new THREE.Euler(0, yaw.current, 0, "YXZ");
      move.applyEuler(yawOnly);

      const base =
        move.x && !move.z ? STRAFE_SPEED :
        move.z && !move.x ? WALK_SPEED :
        Math.min(WALK_SPEED, STRAFE_SPEED);

      const delta = move.multiplyScalar(base * dt);
      const desired = camera.position.clone().add(delta);

      desired.x = THREE.MathUtils.clamp(desired.x, bounds.minX, bounds.maxX);
      desired.z = THREE.MathUtils.clamp(desired.z, bounds.minZ, bounds.maxZ);

      const currentCenter = new THREE.Vector3(camera.position.x, 0, camera.position.z);
      const desiredCenter = new THREE.Vector3(desired.x, 0, desired.z);
      const currentSphere = new THREE.Sphere(currentCenter, PLAYER_RADIUS);
      const desiredSphere = new THREE.Sphere(desiredCenter, PLAYER_RADIUS);

      let blocked = false;
      for (const box of playgroundColliders) {
        const wasInside = box.intersectsSphere(currentSphere);
        const willInside = box.intersectsSphere(desiredSphere);
        if (!wasInside && willInside) { blocked = true; break; }
      }

      if (!blocked) {
        let groundY = 0;
        if (groundMeshes.length > 0) {
          const raycaster = raycasterRef.current;
          originRef.current.set(desired.x, 100, desired.z);
          raycaster.set(originRef.current, new THREE.Vector3(0, -1, 0));
          const hits = raycaster.intersectObjects(groundMeshes, true);
          if (hits.length > 0) groundY = hits[0].point.y;
        }
        camera.position.set(desired.x, groundY + EYE_OFFSET, desired.z);
      } else {
        isMoving = false;
      }
    } else {
      if (groundMeshes.length > 0) {
        const raycaster = raycasterRef.current;
        originRef.current.set(camera.position.x, 100, camera.position.z);
        raycaster.set(originRef.current, new THREE.Vector3(0, -1, 0));
        const hits = raycaster.intersectObjects(groundMeshes, true);
        if (hits.length > 0) {
          const groundY = hits[0].point.y;
          camera.position.y = groundY + EYE_OFFSET;
        }
      }
    }

    if (onWalkStateChange && isMoving !== wasMovingRef.current) {
      wasMovingRef.current = isMoving;
      onWalkStateChange(isMoving);
    }
  });

  return null;
}
 